import { Paper } from '@mui/material';
import axios from 'axios';
import ActionsUsersRenderer from 'components/AgGrid/ActionsUsersRenderer';
import AgGrid from 'components/AgGrid/AgGrid';
import BooleanRenderer from 'components/AgGrid/BooleanRenderer';
import PhoneRenderer from 'components/AgGrid/PhoneRenderer';
import TypeProfilRenderer from 'components/AgGrid/TypeProfilRenderer';
import TopBar from 'components/Layout/TopBar';
import { formatPhoneNumber, logError } from 'components/Utils/FormatData';
import { URL_API } from 'utils/globalConfig';
import useAuthz from 'hooks/useAuthz';
import React, { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useNavigate } from 'react-router';
import { ClipLoader } from 'react-spinners';
import MySelect from '../../components/Layout/MySelect';
import MyButton from '../../components/Layout/MyButton';
import useAccountValidation from '../../hooks/useAccountValidation';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import AccountDeletedRenderer from '../../components/AgGrid/AccountDeletedRenderer';

const Utilisateurs = (props) => {
  const alert = useAlert();
  const nbAccounts = useAccountValidation();
  const [needReloadDatas, setNeedReloadDatas] = useState(false);
  const [users, setUsers] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authz, user, profil] = useAuthz();
  const navigate = useNavigate();
  const [selectedValue, setSelectedValue] = useState({ filterSelected: 1 });
  const columnDefs = [
    {
      headerName: '',
      field: '',
      flex: 1,
      minWidth: 125,
      sortable: false,
      cellRenderer: 'actionsUsersRenderer',
      cellRendererParams: { setNeedReloadDatas: setNeedReloadDatas },
    },
    { headerName: 'login', field: 'login', flex: 1, minWidth: 200, sortable: true }, {
      headerName: 'type profil',
      field: 'profil.type',
      flex: 1,
      minWidth: 200,
      sortable: true,
    },
    {
      headerName: 'nom prénom / raison sociale',
      field: 'nom',
      flex: 1,
      minWidth: 250,
      sortable: true,
      valueFormatter: params => params.data.raison_sociale ? params.data.raison_sociale : params.data.nom && params.data.prenom ? params.data.nom + ' ' + params.data.prenom : '-',
    },
    {
      headerName: 'email',
      field: 'email',
      flex: 1,
      minWidth: 250,
      sortable: true,
    },
    {
      headerName: 'compte',
      field: 'deleted',
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueFormatter: params => params.data.deleted ? 'true' : params.data.validated ? 'false' : 'not_valid',
      cellRenderer: 'accountDeletedRenderer',
    },
    {
      headerName: 'n° cerfa',
      field: 'num_cerfa',
      flex: 1,
      minWidth: 125,
      sortable: true,
      valueFormatter: params => params.data.num_cerfa ? params.data.num_cerfa : '-',
    },
    {
      headerName: 'n° siret',
      field: 'num_siren',
      flex: 1,
      minWidth: 125,
      sortable: true,
      valueFormatter: params => params.data.num_siren ? params.data.num_siren : '-',
    },
    {
      headerName: 'n° TVA intracommunautaire',
      field: 'num_tva',
      flex: 1,
      minWidth: 125,
      sortable: true,
      valueFormatter: params => params.data.num_tva ? params.data.num_tva : '-',
    },
    {
      headerName: 'adresse',
      field: 'adresse',
      flex: 1,
      minWidth: 225,
      sortable: true,
      valueFormatter: params => params.data.adresse ? params.data.adresse : '-',
    },
    {
      headerName: 'complément adresse',
      field: 'complement_adresse',
      flex: 1,
      minWidth: 225,
      sortable: true,
      valueFormatter: params => params.data.complement_adresse ? params.data.complement_adresse : '-',
    },
    {
      headerName: 'code postal',
      field: 'code_postal',
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueFormatter: params => params.data.code_postal ? params.data.code_postal : '-',
    },
    {
      headerName: 'commune',
      field: 'commune',
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueFormatter: params => params.data.commune ? params.data.commune : '-',
    },
    {
      headerName: 'pays', field: 'pays', flex: 1, minWidth: 150, sortable: true, valueFormatter: params => params.data.pays ? params.data.pays : '-',
    },
    {
      headerName: 'telephone n°1',
      field: 'telephone_1',
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueFormatter: params => params.data.telephone_1 ?? '-',
    },
    {
      headerName: 'telephone n°2',
      field: 'telephone_2',
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueFormatter: params => params.data.telephone_2 ?? '-',
    },
  ];
  const agGridOptions = {
    suppressCellSelection: true, suppressHorizontalScroll: false, animateRows: true, rowClassRules: {
      'user-desactivate': function(params) {
        return params.data.deleted === true;
      },
    },
  };
  const frameworkComponents = {
    typeProfilRenderer: TypeProfilRenderer,
    phoneRenderer: PhoneRenderer,
    actionsUsersRenderer: ActionsUsersRenderer,
    accountDeletedRenderer: AccountDeletedRenderer,
    statutRenderer: BooleanRenderer,
  };

  useEffect(() => {
    if (profil.profil !== 1) {
      alert.error('Vous n\'êtes pas autorisé à accéder au module à la gestion des utilisateurs');
      navigate('/');
    } else {
      document.title = 'TE - Gestion des utilisateurs';
      if (!users) {
        getListeUtilisateurs();
      }
    }
  }, []);
  useEffect(() => {
    if (needReloadDatas) {
      getListeUtilisateurs();
      setNeedReloadDatas(false);
    }
  }, [needReloadDatas]);

  const changeSelectValue = (e) => {
    setSelectedValue({ ...selectedValue, [e.target.name]: e.target.value });
    setNeedReloadDatas(true);
  };
  const getListeUtilisateurs = () => {
    setIsLoading(true);
    let deleted;
    if (selectedValue.filterSelected === 3) deleted = ''; else deleted = selectedValue.filterSelected !== 1;
    axios({ method: 'get', url: URL_API + '/utilisateur/liste', params: { deleted: deleted } })
      .then((response) => {
        setIsLoading(false);
        setUsers(response.data);
      })
      .catch((error) => {
        setIsLoading(false);
        logError(alert, error);
      });
  };
  const handleCreateUser = () => {
    navigate('/panneau-administration/utilisateur/creation');
  };

  return (<div style={{ height: '100%', width: '100%' }}>
    <TopBar title='Gestion des utilisateurs' className='topbar-contenu' color={props.params.color}
            buttons={
              <div className='topbar-buttons'>
                <MyButton onClick={handleCreateUser} icon={faPlus} label={'Créer un utilisateur'} />
                <MyButton onClick={() => navigate('/panneau-administration')} icon={''} label={'Retour'} />
              </div>
            }
            filterContainer={<>&nbsp;
              <MySelect name={'filterSelected'} label={'Filtre'}
                        datas={[{ key: 1, value: 'Utilisateurs activés' }, { key: 2, value: 'Utilisateurs désactivés' }, { key: 3, value: 'Tous les utilisateurs' }]}
                        value={selectedValue.filterSelected}
                        firstValue={{ key: 3, value: 'Tous les utilisateurs' }} handleChange={changeSelectValue} />
            </>}
    />

    <div className='row' style={{ margin: 0, overflow: 'hidden' }}>
      <ClipLoader color='#0094ca' loading={isLoading} size={75} css={'display: flex; margin: 25px;justify-content: center;'} />
      {!isLoading && users && <Paper style={{ height: '100%', width: '100%', borderTop: '1px solid #4db4da' }}>
        <AgGrid
          key={'ag-grid-users'}
          rowData={users}
          columnDefs={columnDefs}
          rowHeight={40}
          pagination={false}
          frameworkComponents={frameworkComponents}
          gridOptions={agGridOptions}
          sortable={false}
          agGridName='ag-grid-overflow'
        />
      </Paper>}
    </div>
  </div>);
};

export default Utilisateurs;
